import { Animal, type CountryCode } from 'shared/@types';

export type PedigreeRegistryProvider =
  | 'skk_hunddata'
  | 'nkk_dogweb'
  | 'dkk_hundeweb'
  | 'sverak_findus'
  | 'nrr_min_katt'
  | 'fdkat';

export type PedigreeRegistryLink = {
  provider: PedigreeRegistryProvider;
  href: string;
  registrationId: string;
  isDirect: boolean;
};

type PedigreeRegistryListing = {
  animal: Animal | null;
  user: {
    country: CountryCode;
  };
};

const pedigreeRegistryHosts: Record<PedigreeRegistryProvider, string> = {
  skk_hunddata: 'hundar.skk.se',
  nkk_dogweb: 'www.dogweb.no',
  dkk_hundeweb: 'www.hundeweb.dk',
  sverak_findus: 'findus.sverak.se',
  nrr_min_katt: 'katt.nrr.no',
  fdkat: 'www.fdkat.dk',
};

const dogPedigreeRegistryProviders: PedigreeRegistryProvider[] = [
  'skk_hunddata',
  'nkk_dogweb',
  'dkk_hundeweb',
];

const catPedigreeRegistryProviders: PedigreeRegistryProvider[] = [
  'sverak_findus',
  'nrr_min_katt',
  'fdkat',
];

export function normalizePedigreeRegistrationId(registrationId?: string | null) {
  const normalized = registrationId?.trim().replace(/\s+/g, '').toUpperCase() ?? '';

  return normalized.length > 0 ? normalized : null;
}

// Matches the backend's ISO 15-digit microchip rule in ValidRegistrationId.
export function isPedigreeChipNumber(registrationId: string) {
  return /^\d{15}$/.test(registrationId);
}

export function formatPedigreeRegistrationId(registrationId: string) {
  return isPedigreeChipNumber(registrationId)
    ? registrationId.replace(/(\d{3})(?=\d)/g, '$1 ')
    : registrationId;
}

function normalizePedigreeDirectHref(provider: PedigreeRegistryProvider, href?: string | null) {
  if (!href) {
    return null;
  }

  try {
    const url = new URL(href);

    if (
      url.protocol !== 'https:' ||
      url.host !== pedigreeRegistryHosts[provider] ||
      url.username ||
      url.password ||
      url.hash ||
      !isPedigreeRegistryUrl(provider, url)
    ) {
      return null;
    }

    return url.toString();
  } catch {
    return null;
  }
}

function isPedigreeRegistryUrl(provider: PedigreeRegistryProvider, url: URL) {
  switch (provider) {
    case 'skk_hunddata':
      return (
        url.pathname.toLowerCase() === '/hunddata/hund.aspx' &&
        isNumericIdentifier(getSearchParam(url, 'hundid', true))
      );
    case 'sverak_findus':
      return /^\/stambok\/visa\/\d+\/?$/.test(url.pathname.toLowerCase());
    case 'nkk_dogweb':
      return (
        url.pathname.toLowerCase() === '/hundedatabase/dog' &&
        hasIdentifier(getSearchParam(url, 'id', true))
      );
    case 'nrr_min_katt':
      return (
        url.pathname.toLowerCase() === '/katter/perusnaytto_kissa.aspx' &&
        isNumericIdentifier(getSearchParam(url, 'id', true))
      );
    case 'dkk_hundeweb':
      return (
        url.pathname.toLowerCase() === '/hundedatabase/hund' &&
        hasIdentifier(getSearchParam(url, 'id', true))
      );
    case 'fdkat':
      return (
        url.pathname.toLowerCase() === '/perusnaytto_kissa.aspx' &&
        isNumericIdentifier(getSearchParam(url, 'id', true))
      );
  }
}

function getSearchParam(url: URL, name: string, caseInsensitive = false) {
  let matchingValue: string | null = null;

  url.searchParams.forEach((value, key) => {
    if (
      matchingValue === null &&
      (caseInsensitive ? key.toLowerCase() === name.toLowerCase() : key === name)
    ) {
      matchingValue = value;
    }
  });

  return matchingValue;
}

function hasIdentifier(value?: string | null) {
  return Boolean(value?.trim());
}

function isNumericIdentifier(value?: string | null) {
  return Boolean(value && /^\d+$/.test(value));
}

export function getPedigreeRegistryLink(
  listing: PedigreeRegistryListing,
  pedigreeRegistrationId?: string | null,
  options: { directHref?: string | null } = {},
) {
  const registrationId = normalizePedigreeRegistrationId(pedigreeRegistrationId);
  const provider = getPedigreeRegistryProvider(listing, options.directHref);
  const href = provider ? normalizePedigreeDirectHref(provider, options.directHref) : null;

  if (!registrationId || !href || !provider) {
    return null;
  }

  return {
    provider,
    href,
    registrationId,
    isDirect: true,
  };
}

function getPedigreeRegistryProvider(
  listing: PedigreeRegistryListing,
  directHref?: string | null,
): PedigreeRegistryProvider | null {
  if (!directHref) {
    return null;
  }

  try {
    const hostname = new URL(directHref).hostname.toLowerCase();
    const provider = Object.entries(pedigreeRegistryHosts).find(
      ([, registryHostname]) => registryHostname === hostname,
    )?.[0] as PedigreeRegistryProvider | undefined;

    if (!provider) {
      return null;
    }

    if (listing.animal === Animal.Dog && dogPedigreeRegistryProviders.includes(provider)) {
      return provider;
    }

    if (listing.animal === Animal.Cat && catPedigreeRegistryProviders.includes(provider)) {
      return provider;
    }

    return null;
  } catch {
    return null;
  }
}
